import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate, Link } from "react-router-dom";
import { UtensilsCrossed, ChevronDown, Menu, MapPin, GitCompare, Mail, Info, Lightbulb, BookOpen, Heart } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const Header = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [regionsOpen, setRegionsOpen] = useState(false);
  const [comparatifsOpen, setComparatifsOpen] = useState(false);

  // Robust cleanup for iOS Safari scroll lock bug
  const cleanupScrollLock = useCallback(() => {
    // Multiple cleanup attempts with increasing delays for iOS reliability
    const cleanupStyles = () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.pointerEvents = '';
      document.body.style.touchAction = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.removeAttribute('data-scroll-locked');
      document.documentElement.style.overflow = '';
      document.documentElement.style.position = '';
    };

    // Immediate cleanup
    cleanupStyles();
    
    // Delayed cleanups for iOS Safari
    setTimeout(cleanupStyles, 50);
    setTimeout(cleanupStyles, 150);
    setTimeout(cleanupStyles, 300);
  }, []);

  const handleMobileMenuChange = (open: boolean) => {
    setMobileMenuOpen(open);
    
    if (!open) {
      // Close collapsibles when menu closes
      setRegionsOpen(false);
      setComparatifsOpen(false);
      // Trigger cleanup
      cleanupScrollLock();
    }
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
    cleanupScrollLock();
  };

  const handleAnchorClick = (anchor: string) => {
    setMobileMenuOpen(false);
    cleanupScrollLock();
    // Small delay to ensure menu is closed before navigating
    setTimeout(() => {
      window.location.href = anchor;
    }, 100);
  };

  // Cleanup on unmount and window resize
  useEffect(() => {
    const handleResize = () => {
      if (mobileMenuOpen && window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
        cleanupScrollLock();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cleanupScrollLock();
    };
  }, [mobileMenuOpen, cleanupScrollLock]);

  const regions = [
    { path: "/lausanne", label: "Lausanne" },
    { path: "/geneve", label: "Genève" },
    { path: "/fribourg", label: "Fribourg" },
    { path: "/neuchatel", label: "Neuchâtel" },
    { path: "/sion", label: "Sion" },
  ];

  const comparatifs = [
    { path: "/comparatif/piktable-vs-mylocalina", label: "vs MyLOCALINA" },
    { path: "/comparatif/piktable-vs-foratable", label: "vs Foratable" },
    { path: "/comparatif/piktable-vs-aleno", label: "vs aleno" },
    { path: "/comparatif/piktable-vs-thefork", label: "vs TheFork" },
    { path: "/comparatif/piktable-vs-opentable", label: "vs OpenTable" },
    { path: "/comparatif/piktable-vs-zenchef", label: "vs Zenchef" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border">
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <UtensilsCrossed className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
            <h1 className="font-playfair text-xl sm:text-2xl font-bold text-foreground">
              Piktable
            </h1>
          </Link>
          
          {/* Desktop navigation - visible from lg (1024px+) */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            <a href="/#comment" className="text-foreground hover:text-primary transition-colors text-sm xl:text-base">
              Fonctionnement
            </a>
            <a href="/#pourquoi" className="flex items-center gap-1.5 text-foreground hover:text-primary transition-colors text-sm xl:text-base">
              Piktable <Heart className="w-4 h-4 text-primary fill-primary" />
            </a>
            <Link to="/pricing" className="text-foreground hover:text-primary transition-colors text-sm xl:text-base">
              Tarifs
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-foreground hover:text-primary transition-colors text-sm xl:text-base">
                Régions <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {regions.map((region) => (
                  <DropdownMenuItem key={region.path} asChild>
                    <Link to={region.path}>{region.label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-foreground hover:text-primary transition-colors text-sm xl:text-base">
                Comparatifs <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {comparatifs.map((comp) => (
                  <DropdownMenuItem key={comp.path} asChild>
                    <Link to={comp.path}>{comp.label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/blog" className="text-foreground hover:text-primary transition-colors text-sm xl:text-base">
              Blog
            </Link>
            <Link to="/contact" className="text-foreground hover:text-primary transition-colors text-sm xl:text-base">
              Contact
            </Link>
          </nav>
          
          {/* Desktop CTA buttons - visible from lg (1024px+) */}
          <div className="hidden lg:flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/login")}>
              Connexion
            </Button>
            <Button 
              size="sm"
              onClick={() => navigate("/signup")}
              className="relative animate-subtle-pulse bg-primary text-white hover:bg-primary/90"
            >
              Essayer gratuitement
              <Badge className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0.5 bg-accent text-accent-foreground border-0">
                30 jours offerts
              </Badge>
            </Button>
          </div>

          {/* Mobile/Tablet CTA + hamburger - visible below lg (< 1024px) */}
          <div className="flex lg:hidden items-center gap-2">
            <Button 
              size="sm"
              onClick={() => navigate("/signup")}
              className="relative animate-subtle-pulse text-xs bg-primary text-white hover:bg-primary/90"
            >
              Essai gratuit
              <Badge className="absolute -top-2 -right-2 text-[9px] px-1 py-0.5 bg-accent text-accent-foreground border-0">
                30 jours
              </Badge>
            </Button>
            
            <Sheet open={mobileMenuOpen} onOpenChange={handleMobileMenuChange}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="px-2">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent 
                side="right" 
                className="w-[300px] p-0 flex flex-col"
                onCloseAutoFocus={(e) => {
                  e.preventDefault();
                  cleanupScrollLock();
                }}
              >
                <SheetHeader className="p-4 border-b border-border flex-shrink-0">
                  <SheetTitle className="flex items-center gap-2">
                    <UtensilsCrossed className="w-5 h-5 text-primary" />
                    Piktable
                  </SheetTitle>
                </SheetHeader>
                
                <ScrollArea className="flex-1 h-[calc(100vh-140px)]">
                  <nav className="flex flex-col p-4">
                    {/* Main navigation links with icons and larger touch targets */}
                    <button
                      onClick={() => handleAnchorClick("/#comment")}
                      className="flex items-center gap-3 text-foreground hover:text-primary hover:bg-muted transition-colors py-3 px-2 rounded-md text-left w-full min-h-[48px]"
                    >
                      <Info className="w-5 h-5 text-muted-foreground" />
                      Fonctionnement
                    </button>
                    <button
                      onClick={() => handleAnchorClick("/#pourquoi")}
                      className="flex items-center gap-3 text-foreground hover:text-primary hover:bg-muted transition-colors py-3 px-2 rounded-md text-left w-full min-h-[48px]"
                    >
                      <Heart className="w-5 h-5 text-primary fill-primary" />
                      Piktable
                    </button>
                    <Link
                      to="/pricing"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        cleanupScrollLock();
                      }}
                      className="flex items-center gap-3 text-foreground hover:text-primary hover:bg-muted transition-colors py-3 px-2 rounded-md min-h-[48px]"
                    >
                      <Info className="w-5 h-5 text-muted-foreground" />
                      Tarifs
                    </Link>
                    
                    {/* Regions - Collapsible section */}
                    <Collapsible open={regionsOpen} onOpenChange={setRegionsOpen} className="mt-2">
                      <CollapsibleTrigger className="flex items-center justify-between w-full py-3 px-2 rounded-md hover:bg-muted transition-colors min-h-[48px]">
                        <div className="flex items-center gap-3">
                          <MapPin className="w-5 h-5 text-muted-foreground" />
                          <span className="text-foreground">Régions</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${regionsOpen ? 'rotate-180' : ''}`} />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
                        <div className="flex flex-col pl-10 py-1">
                          {regions.map((region) => (
                            <Link
                              key={region.path}
                              to={region.path}
                              onClick={() => {
                                setMobileMenuOpen(false);
                                cleanupScrollLock();
                              }}
                              className="text-foreground hover:text-primary hover:bg-muted transition-colors py-2.5 px-2 rounded-md min-h-[44px] flex items-center"
                            >
                              {region.label}
                            </Link>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                    
                    {/* Comparatifs - Collapsible section */}
                    <Collapsible open={comparatifsOpen} onOpenChange={setComparatifsOpen}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full py-3 px-2 rounded-md hover:bg-muted transition-colors min-h-[48px]">
                        <div className="flex items-center gap-3">
                          <GitCompare className="w-5 h-5 text-muted-foreground" />
                          <span className="text-foreground">Comparatifs</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${comparatifsOpen ? 'rotate-180' : ''}`} />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
                        <div className="flex flex-col pl-10 py-1">
                          {comparatifs.map((comp) => (
                            <Link
                              key={comp.path}
                              to={comp.path}
                              onClick={() => {
                                setMobileMenuOpen(false);
                                cleanupScrollLock();
                              }}
                              className="text-foreground hover:text-primary hover:bg-muted transition-colors py-2.5 px-2 rounded-md min-h-[44px] flex items-center"
                            >
                              {comp.label}
                            </Link>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                    
                    {/* Blog link */}
                    <Link
                      to="/blog"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        cleanupScrollLock();
                      }}
                      className="flex items-center gap-3 text-foreground hover:text-primary hover:bg-muted transition-colors py-3 px-2 rounded-md mt-2 min-h-[48px]"
                    >
                      <BookOpen className="w-5 h-5 text-muted-foreground" />
                      Blog
                    </Link>

                    {/* Contact link */}
                    <Link
                      to="/contact"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        cleanupScrollLock();
                      }}
                      className="flex items-center gap-3 text-foreground hover:text-primary hover:bg-muted transition-colors py-3 px-2 rounded-md min-h-[48px]"
                    >
                      <Mail className="w-5 h-5 text-muted-foreground" />
                      Contact
                    </Link>
                  </nav>
                </ScrollArea>
                
                {/* Fixed bottom buttons */}
                <div className="flex flex-col gap-2 p-4 border-t border-border bg-background flex-shrink-0">
                  <Button 
                    variant="outline" 
                    onClick={() => handleNavigate("/login")} 
                    className="w-full min-h-[48px]"
                  >
                    Connexion
                  </Button>
                  <Button 
                    variant="default" 
                    onClick={() => handleNavigate("/signup")} 
                    className="w-full min-h-[48px]"
                  >
                    Créer un compte gratuit
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
